import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Box, Button, Stack } from '@eneco/sparky';

import type { FlowNavigationContent } from './navigation-content.types';
import { NavigationCloseButton, NavigationHelpButton } from '../../components';
import { useStepContent } from '../../hooks';
import { FlowContext, FlowRoute } from '../../types';
import { FlowNavigation } from '../../utils';
import { readNameLabelList } from '../../utils/readNameLabelList';

export interface Props<T extends FlowContext> {
  routes: FlowRoute<T>[];
  componentName?: string;
  showCategories?: boolean;
}
/**
 * This generic `Navigation` component is used to render the navigation for the flow.
 * There is no such generic block in the CMS, which is why `componentName` is variable:
 * each flow names its navigation after itself.
 * If custom rendering is needed, a custom Navigation component should be created on the flow level.
 */
export const Navigation = <T extends FlowContext = FlowContext>({
  routes,
  componentName = 'Navigation',
  showCategories = true,
}: Props<T>) => {
  const textualData = useStepContent<FlowNavigationContent>(componentName) ?? {};
  const { helpDialog, closeDialog, closeRedirectLink, cancelButtonText } = textualData;

  return (
    <FlowNavigation<T>
      routes={routes}
      backLinkLabel={textualData.previousStepText ?? ''}
      showCategories={showCategories}
      progressIndicatorLabels={readNameLabelList(textualData.navigationSteps)}>
      <Stack direction="row" gap="1">
        {helpDialog?.title || helpDialog?.content ? (
          <TrackedDialog
            title={helpDialog.title}
            trigger={<NavigationHelpButton label={helpDialog.triggerText ?? ''} />}>
            <Box paddingBottom="8">
              <RichText html={helpDialog.content} />
            </Box>
          </TrackedDialog>
        ) : null}

        {closeDialog?.title || closeDialog?.content ? (
          <TrackedDialog
            title={closeDialog.title}
            trigger={<NavigationCloseButton label={closeDialog.triggerText ?? ''} />}>
            <Stack direction="column" gap="2">
              <Box paddingBottom="8">
                <RichText html={closeDialog.content} />
              </Box>
              <Stack direction={{ initial: 'column', lg: 'row' }} gap="2" wrap>
                {closeRedirectLink?.href ? (
                  <Button
                    onClick={() => {
                      window.location.href = closeRedirectLink.href ?? '';
                    }}>
                    {closeRedirectLink.text}
                  </Button>
                ) : null}
                {cancelButtonText ? (
                  <TrackedDialog.Close asChild>
                    <Button action="secondary">{cancelButtonText}</Button>
                  </TrackedDialog.Close>
                ) : null}
              </Stack>
            </Stack>
          </TrackedDialog>
        ) : null}
      </Stack>
    </FlowNavigation>
  );
};
